import { ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Descriptions, Modal, Row, Space, Tag, Typography } from 'antd';
import { NativeButtonProps } from 'antd/lib/button/button';
import { EditOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Actions from 'components/Actions/Actions';
import { TooltipButton } from 'components/TooltipButton/TooltipButton';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useContinueLoggerProjectMutation, useEndLoggerWorkMutation } from 'app/services/loggers';
import { actions as loggerAccess, selectors as loggerAccessSelectors } from 'app/services/loggers/slice';
import { Drawers } from 'app/types/drawer';
import { Paths } from 'constants/index';
import { Status } from 'constants/site';
import { useOpenDrawerHandler, useRevokeOnHistoryNavigation, useScreenBreakpoint } from 'hooks';
import { formatDateTime } from 'utils/datetime';
import { showSuccess } from 'utils/notifications';
import './overview.less';

type ActionDefinition = {
  key: string,
  title: string,
  primary?: boolean,
  buttonProps: NativeButtonProps,
};


const PublicOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isMobile } = useScreenBreakpoint();
  const { barcode = '' } = useParams<{ barcode: string }>();
  const access = useAppSelector(loggerAccessSelectors.getLoggerAccess(barcode));
  const viaHistory = useRevokeOnHistoryNavigation();
  const [ continueProject, { isLoading } ] = useContinueLoggerProjectMutation();
  const [ endWork, { isLoading: isEndingWork } ] = useEndLoggerWorkMutation();
  const openEdit = useOpenDrawerHandler({ key: Drawers.EDIT_LOGGER_PROJECT, props: { barcode }, title: t('forms.workplace.edit') });

  const codePath = Paths.LOGGERS_OVERVIEW_CODE.replace(':barcode', encodeURIComponent(barcode));
  const newWorkplacePath = Paths.LOGGERS_OVERVIEW_WORKPLACE.replace(':barcode', encodeURIComponent(barcode));

  if (!access || viaHistory) return <Navigate to={codePath} replace />;

  const { overview, code } = access;
  const helmet = <Helmet title={`Kvalilog > ${t('pages.loggers.overview')}`} />;

  const handleFailure = (error: unknown) => {
    if ((error as { status?: number | string } | undefined)?.status !== 400) return;
    dispatch(loggerAccess.revoke());
    navigate(codePath, { replace: true });
  };

  const handleContinue = async () => {
    try {
      await continueProject({ barcode, code }).unwrap();
      dispatch(loggerAccess.revoke());
      showSuccess(t('pages.loggers.workplace.continued'));
      navigate(codePath, { replace: true });
    } catch (error) {
      handleFailure(error);
    }
  };

  const handleEndWork = () => {
    Modal.confirm({
      title: t('common.confirmation'),
      content: t('pages.loggers.workplace.endworkconfirm'),
      okText: t('common.confirm'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await endWork({ barcode, code }).unwrap();
          dispatch(loggerAccess.revoke());
          showSuccess(t('pages.loggers.workplace.workended'));
          navigate(codePath, { replace: true });
        } catch (error) {
          handleFailure(error);
        }
      },
    });
  };

  const project = overview.Project;

  // Nothing to show and nothing to act on without a workplace: the worker goes straight to the form that
  // creates one, which is also where "Add new work" below leads.
  if (!overview.HasActiveProject || !project) return <Navigate to={newWorkplacePath} replace />;

  // Edit, Continue work, Add new work, End work - in that order everywhere, with the one that cannot be
  // undone last. Edit is not in here because on a phone it stays the pencil in the card header.
  const actionDefinitions: ActionDefinition[] = [
    { key: 'continue', title: t('pages.loggers.workplace.continue'), primary: true, buttonProps: { loading: isLoading, onClick: handleContinue } },
    { key: 'addnew', title: t('pages.loggers.workplace.addnew'), buttonProps: { onClick: () => navigate(newWorkplacePath, { replace: true }) } },
    { key: 'endwork', title: t('pages.loggers.workplace.endwork'), buttonProps: { danger: true, loading: isEndingWork, onClick: handleEndWork } },
  ];

  // Plain text labels, no icons: in a list of four actions an icon on one of them only looks inconsistent.
  const headerActions = [
    <TooltipButton key="edit" title={t('common.edit')} buttonProps={{ onClick: openEdit, type: 'link' }} />,
    ...actionDefinitions.map((action) => (
      <TooltipButton key={action.key} title={action.title} buttonProps={{ ...action.buttonProps, type: 'link' }} />
    )),
  ];

  const mobileActions = actionDefinitions.map((action) => (
    <TooltipButton
      key={action.key}
      title={action.title}
      buttonProps={{ ...action.buttonProps, type: action.primary ? 'primary' : 'default', block: true }}
    />
  ));

  // Sample status next to the title, with the same colours and texts the logged-in Details uses for it.
  const statusTags = [
    { value: Status.NAN, color: 'default', message: t('common.nan') },
    { value: Status.NOK, color: 'error', message: t('common.nok') },
    { value: Status.OK, color: 'success', message: t('common.ok') },
    { value: Status.WIP, color: 'blue', message: t('common.wip') },
    { value: Status.TAKEN, color: 'cyan', message: t('common.taken') }
  ];
  const statusTag = statusTags.find((tag) => tag.value == project.Status);

  const header = (
    <Row justify="space-between" align="middle">
      <Col>
        <Space>
          <div className="title">{project.WorkPlace}</div>
          {statusTag && <Tag color={statusTag.color}>{statusTag.message}</Tag>}
        </Space>
      </Col>
      <Col>
        {isMobile
          ? <Button type="link" icon={<EditOutlined />} onClick={openEdit} className="logger-overview-edit" />
          : <Actions actions={headerActions} />}
      </Col>
    </Row>
  );

  const tools = overview.Tools ?? [];

  // Details as table rows ("label: value") on every screen size, with the same labels as the private
  // workplace view (entities.project); long values wrap inside the card instead of overflowing it.
  const rows: { key: string, value: ReactNode }[] = [
    { key: 'address', value: project.Address },
    { key: 'start', value: formatDateTime(project.Start) },
    { key: 'workplace', value: project.WorkPlace },
    { key: 'workname', value: project.WorkName },
    { key: 'workername', value: project.WorkerName },
    { key: 'code', value: project.Code },
    { key: 'model', value: project.Model },
    // As strings: a limit of 0 is a value, not a missing one, and the renderer falls back on falsy.
    { key: 'lowlimit', value: project.LowLimit?.toString() },
    { key: 'highlimit', value: project.HighLimit?.toString() },
    { key: 'frequency', value: project.Frequency?.toString() },
    { key: 'end', value: project.EndDate ? formatDateTime(project.EndDate) : null },
    {
      key: 'tools',
      value: tools.length === 0 ? null : (
        <Space direction="vertical" size={0}>
          {tools.map((tool) => tool.QrCode ? (
            <Link key={tool.Id} to={Paths.TOOLS_OVERVIEW.replace(':barcode', encodeURIComponent(tool.QrCode))}>
              {tool.Label}
            </Link>
          ) : (
            <Typography.Text key={tool.Id}>{tool.Label}</Typography.Text>
          ))}
        </Space>
      )
    },
  ];

  return (
    <>
      {helmet}
      <Row className="logger-overview">
        <Col span={24}>
          <Card title={header}>
            <Descriptions column={1} size="small" layout="horizontal" colon className="logger-overview-details">
              {rows.map((row) => (
                <Descriptions.Item key={row.key} label={t(`entities.project.${row.key}`)}>
                  <Typography.Text strong>{row.value || '-'}</Typography.Text>
                </Descriptions.Item>
              ))}
            </Descriptions>
            {isMobile && (
              <Space direction="vertical" size="small" className="logger-overview-actions">
                {mobileActions}
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PublicOverview;
